import {useCallback, useState} from 'react'
import {useInstallPrompt} from "@/hooks/useInstallPrompt.ts";
import {useBrowserLanguage} from "@/hooks/useBrowserLanguage.ts";
import {trans} from "@/lib/translator.ts";
import {ShareIcon, SquareChevronDownIcon, SquarePlusIcon} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@/components/ui/dialog.tsx";
import {DAYS_MS} from "@/lib/constants.ts";
import {useIsMobile} from "@/hooks/useIsMobile.ts";

const DISMISSED_KEY = 'pwa-install-dismissed-at-v5'
const RE_PROMPT_AFTER_MS = DAYS_MS;

function isIos() {
    return /iphone|ipad|ipod/i.test(window.navigator.userAgent)
}

const ACTION_CLASS = "flex flex-row items-center justify-start gap-2 shadow-xs rounded-xl px-4 py-2 bg-muted";

function wasRecentlyDismissed() {
    const stored = localStorage.getItem(DISMISSED_KEY)
    if (!stored) return false
    return Date.now() - Number(stored) < RE_PROMPT_AFTER_MS
}

export function InstallPrompt() {
    const {canInstall, isInstalled, promptInstall} = useInstallPrompt()
    const l = useBrowserLanguage()
    const [dismissed, setDismissed] = useState(wasRecentlyDismissed())
    const isMobile = useIsMobile();

    const dismiss = useCallback(() => {
        localStorage.setItem(DISMISSED_KEY, String(Date.now()))
        setDismissed(true)
    }, []);
    const ios = isIos();

    return (<Dialog open={!dismissed && (canInstall || (ios && isMobile)) && !isInstalled} onOpenChange={dismiss}>
        <DialogContent>
            <DialogHeader>
                <DialogTitle className="font-semibold">{trans('pwaInstallApp', l)}</DialogTitle>
            </DialogHeader>
            {ios && (
                <div className="flex-1 flex flex-col items-stretch justify-between gap-2">
                    <div className={ACTION_CLASS}>
                        <span className="text-muted-foreground w-3">1.</span>
                        <ShareIcon className="text-blue-500"/>
                        <span className="font-medium">{trans('pwaShare', l)}</span>
                    </div>
                    <div className={ACTION_CLASS}>
                        <span className="text-muted-foreground w-3">2.</span>
                        <SquareChevronDownIcon className="text-blue-500"/>
                        <span className="font-medium">{trans('pwaShowMore', l)}</span>
                    </div>
                    <div className={ACTION_CLASS}>
                        <span className="text-muted-foreground w-3">3.</span>
                        <SquarePlusIcon className="text-blue-500"/>
                        <span className="font-medium">{trans('pwaAddToHomescreen', l)}</span>
                    </div>
                </div>)
            }
            {!ios && (
                <Button variant="outline" size="xl" onClick={() => promptInstall().then(dismiss)}>
                    {trans('pwaInstallNow', l)}
                </Button>
            )}
        </DialogContent>
    </Dialog>);
}
